/**
 * Contract of `/api/community/moderation/*` (Api::CommunityModerationController, Phase 2).
 *
 * Everything here is scoped: a moderator only ever gets back what lies inside
 * the channels they hold `moderate` for; network-wide actions are for admins.
 * Anyone who moderates nothing receives 403 on every endpoint.
 */

/** A measure against a member. `warn` has no effect on rights; the lifts point at the row they undo. */
export type CommunityModerationKind = 'warn' | 'mute' | 'ban' | 'hide' | 'unmute' | 'unban' | 'unhide';
/** What a moderator may impose (the lifts are created through `lift`). */
export type CommunitySanctionKind = 'warn' | 'mute' | 'ban' | 'hide';
export const COMMUNITY_SANCTION_KINDS: readonly CommunitySanctionKind[] = ['warn', 'mute', 'ban', 'hide'];

export type CommunityModerationScopeType = 'CommunityGroup' | 'CommunityChannel';

/** `email` only for admins. */
export interface CommunityModerationUser {
  id: number;
  username: string | null;
  full_name: string | null;
  email?: string | null;
}

/** One row of the append-only protocol. */
export interface CommunityModerationAction {
  id: number;
  kind: CommunityModerationKind;
  user: CommunityModerationUser | null;
  actor: CommunityModerationUser | null;
  scope_type: CommunityModerationScopeType | null;
  scope_id: number | null;
  scope_name: string | null;
  reason: string;
  until: string | null;
  lifts_action_id: number | null;
  /** Null on warnings and lifts (only sanctions can be lifted / expire). */
  lifted: boolean | null;
  expired: boolean | null;
  created_at: string;
}

export interface CommunityModerationActionListResponse {
  data: CommunityModerationAction[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

export interface CommunityModerationActionPayload {
  user_id: number;
  kind: CommunitySanctionKind;
  /** Omit both for a network-wide action (admins only; `hide` is always network-wide). */
  scope_type?: CommunityModerationScopeType | null;
  scope_id?: number | null;
  reason: string;
  until?: string | null;
}

export interface CommunityModerationUserSummary {
  user: CommunityModerationUser;
  active_sanctions: CommunityModerationAction[];
  warnings: CommunityModerationAction[];
  thread_count: number;
  post_count: number;
}

// --- Reports (§22) ------------------------------------------------------------

export type CommunityReportStatus = 'open' | 'in_review' | 'resolved' | 'dismissed';
export const COMMUNITY_REPORT_STATUSES: readonly CommunityReportStatus[] = ['open', 'in_review', 'resolved', 'dismissed'];

export interface CommunityReport {
  id: number;
  reportable_type: 'CommunityThread' | 'CommunityPost';
  reportable_id: number;
  /** The thread the reported content lives in (the thread itself for a thread report). */
  thread_id: number | null;
  channel: { id: number; name: string; group_id: number };
  excerpt: string | null;
  author: CommunityModerationUser | null;
  reporter: CommunityModerationUser | null;
  reason: string;
  status: CommunityReportStatus;
  assignee: CommunityModerationUser | null;
  resolution: string | null;
  resolved_at: string | null;
  resolved_by: CommunityModerationUser | null;
  created_at: string;
  updated_at?: string;
}

export interface CommunityReportListResponse {
  data: CommunityReport[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  /** Open + in review inside the caller's scope, regardless of the filter. */
  open_count: number;
}

export interface CommunityReportUpdatePayload {
  status?: CommunityReportStatus;
  assignee_id?: number | null;
  /** Required by the backend when closing (resolved/dismissed). */
  resolution?: string;
}

// --- Approval queue (§23) -----------------------------------------------------

export interface CommunityQueueThread {
  id: number;
  type: 'thread';
  channel: { id: number; name: string };
  title: string;
  body: string;
  user: CommunityModerationUser | null;
  created_at: string;
  /** Why an automated filter routed this into the queue, if any. */
  flagged_reason: string | null;
}

export interface CommunityQueuePost {
  id: number;
  type: 'post';
  thread_id: number;
  thread_title: string;
  channel: { id: number; name: string };
  body: string;
  user: CommunityModerationUser | null;
  created_at: string;
  /** Why an automated filter routed this into the queue, if any. */
  flagged_reason: string | null;
}

export interface CommunityQueueResponse {
  threads: CommunityQueueThread[];
  posts: CommunityQueuePost[];
  count: number;
}

/** Display name of a moderation user brief. */
export function moderationUserName(u: CommunityModerationUser | null | undefined): string {
  if (!u) return '—';
  return u.full_name || u.username || `#${u.id}`;
}
